import { DataService } from 'src/app/services/data.service';
import { DialogService } from 'src/app/services/dialog.service';
import { DialogFormService } from 'src/app/services/dialog-form.service';
import { AuthService } from 'src/app/services/auth.service';
import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ANY_TAG_NAME, Role, SaveUserDto, Tag, UserUpper } from 'src/app/models/user';
import { CompanyListItemDto } from 'src/app/models/dialog-dto/company-list-item.dto';
import { CustomerListItemDto } from 'src/app/models/dialog-dto/customer-list-item.dto';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filterRolesForClient } from 'src/app/utils/client-scope';
import { forkJoin } from 'rxjs';

interface AddUserDialogData {
  guid: string
}

// Roles that always see everything — the Any-tag is pinned (non-removable)
// in the Sichtbarkeit-Step for these, mirroring the backend's
// forceSeesAllForRole (auth_controller.go PostUser/PutUser).
const ALWAYS_SEES_ALL_ROLES = ['Admin', 'Operator'];

@Component({
  selector: 'app-dialog-add-user',
  templateUrl: './dialog-add-user.component.html',
  styleUrl: './dialog-add-user.component.scss',
})

export class DialogAddUserComponent implements OnInit {
  guid: string = '';
  form: FormGroup;

  // true for a Client-role caller creating/editing a Customer user — the
  // picker is then restricted to the caller's own scope and no Tags picker
  // is shown (only Admin may grant those, including the Any-tag).
  isClient: boolean = false;

  allTags: Tag[] = [];
  allRoles: Role[] = [];

  allClients: CompanyListItemDto[] = [];
  selectedClients: CompanyListItemDto[] = [];
  allCustomers: CustomerListItemDto[] = [];
  selectedCustomers: CustomerListItemDto[] = [];

  selectedRole: string = '';
  selectedTags: Tag[] = [];

  constructor(
    private dialogFormService: DialogFormService,
    private dialogService: DialogService,
    private dataService: DataService,
    private authService: AuthService,
    private dialogRef: MatDialogRef<DialogAddUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddUserDialogData
  ) {
    this.form = this.dialogFormService.newUserForm();

    if(this.data?.guid != undefined && this.data?.guid != "") {
      this.guid = this.data.guid
      this.dataService.getUser(this.data.guid).subscribe(user => {
        const userUpper = user as unknown as UserUpper
        this.form.get("username")?.setValue(userUpper.Username)
        this.form.get("email")?.setValue(userUpper.EMail)
        this.form.get("firstname")?.setValue(userUpper.Firstname)
        this.form.get("lastname")?.setValue(userUpper.Lastname)
        this.form.get("role")?.setValue(userUpper.RoleID)

        this.dataService.getRoleByUser(userUpper.RoleID).subscribe(role => {
          this.selectedRole = role.Name
        })

        this.dataService.getClientsByUser(userUpper.ID).subscribe(clients => {
          this.selectedClients = clients
          this.form.get("clientIds")?.setValue(clients.map(c => c.id))
          this.loadCustomersForSelectedClients()
        })
        this.dataService.getCustomersByUser(userUpper.ID).subscribe(customers => {
          this.selectedCustomers = customers
          this.form.get("customerIds")?.setValue(customers.map(c => c.id))
        })
        this.dataService.getTagsByUser(userUpper.ID).subscribe(tags => {
          this.selectedTags = tags
          this.form.markAsPristine()
        })

        // The Any-tag is never a real user_tags row (see backend) — it's
        // derived purely from SeesAll and has to be synthesized into the
        // displayed selection here. Harmless no-op if allTags (ngOnInit)
        // hasn't loaded yet — that load path calls ensureAnyTagSelected()
        // again once it resolves.
        if (userUpper.SeesAll) {
          this.ensureAnyTagSelected()
        }
      })
    }
  }

  ngOnInit(): void {
    this.isClient = this.authService.checkRole(['Client']);

    if (this.isClient) {
      // Client-role: the picker may only offer the caller's own Clients — the
      // server enforces this too, but the UI should never show a choice it
      // would reject.
      this.dialogService.getMyClientsList().subscribe((clients) => {
        this.allClients = clients;
      });
      this.dataService.getRoles().subscribe((roles) => {
        this.allRoles = filterRolesForClient(roles, true);
      });
    } else {
      this.dialogService.getClientsList().subscribe((clients) => {
        this.allClients = clients;
      });
      this.dataService.getTags().subscribe((tags) => {
        this.allTags = tags
          .filter(t => t.Active !== false)
          .sort((a, b) => a.Name.localeCompare(b.Name));
        this.ensureAnyTagSelected();
      });
      this.dataService.getRoles().subscribe((roles) => {
        this.allRoles = roles;
      });
    }
  }

  // loadCustomersForSelectedClients refreshes the Customer picker options to
  // the union of Customers under all currently selected Clients.
  loadCustomersForSelectedClients() {
    if (this.selectedClients.length === 0) {
      this.allCustomers = [];
      return;
    }
    forkJoin(
      this.selectedClients.map((c) => this.dialogService.getCustomerList(c.id))
    ).subscribe((lists) => {
      const byId = new Map<number, CustomerListItemDto>();
      lists.flat().forEach((customer) => byId.set(customer.id, customer));
      this.allCustomers = Array.from(byId.values());
    });
  }

  // isAnySeesAllRole returns whether the currently selected role always sees
  // everything — the Any-tag is pinned and non-removable in that case.
  isAnySeesAllRole(): boolean {
    return ALWAYS_SEES_ALL_ROLES.includes(this.selectedRole);
  }

  isAnyTag(tag: Tag): boolean {
    return tag.Name === ANY_TAG_NAME;
  }

  // ensureAnyTagSelected adds the real Any-tag object (looked up from
  // allTags) to selectedTags once both are available — used both for
  // prefilling a SeesAll=true user and for pinning it when an Admin/Operator
  // role is chosen.
  private ensureAnyTagSelected() {
    if (this.selectedTags.some(t => this.isAnyTag(t))) {
      return;
    }
    const anyTag = this.allTags.find(t => this.isAnyTag(t));
    if (anyTag) {
      this.selectedTags.push(anyTag);
    }
  }

  save() {
    const seesAll = this.selectedTags.some(t => this.isAnyTag(t));
    const dto: SaveUserDto = {
      username: this.form.get("username")?.value,
      firstname: this.form.get("firstname")?.value,
      lastname: this.form.get("lastname")?.value,
      email: this.form.get("email")?.value,
      role: this.form.get("role")?.value,
      active: this.form.get("active")?.value ?? true,
      clientIds: this.selectedClients.map(c => c.id),
      customerIds: this.selectedCustomers.map(c => c.id),
      seesAll: this.isClient ? false : seesAll,
      // The Any-tag is represented purely via `seesAll`, never sent as a
      // regular Sonderfall-tag.
      tags: this.isClient ? [] : this.selectedTags.filter(t => !this.isAnyTag(t)),
    }

    this.dialogFormService.saveUser(dto, this.guid).subscribe(response => {
      if (response.success) {
        this.dialogRef.close([response.success, dto]);
      }
    })
  }

  undoChanges(formControlName: string, value?: string | number) {
    if (value) {
      this.form.get(formControlName)?.setValue(value);
    } else {
      this.form.get(formControlName)?.setValue('');
    }
  }

  appendTag(tag: Tag) {
    let found = false
    this.selectedTags.forEach(t => {
      if(t.ID == tag.ID) {
        found = true
      }
    })
    if(!found) {
      this.selectedTags.push(tag)
      this.form.markAsDirty()
    }
  }

  removeTag(tag: Tag) {
    // The Any-tag can't be removed while the selected role always sees
    // everything — matches the backend, which would force it right back.
    if (this.isAnyTag(tag) && this.isAnySeesAllRole()) {
      return;
    }
    if(this.selectedTags.includes(tag)) {
      this.selectedTags = this.selectedTags.filter(t => t != tag)
      this.form.markAsDirty()
    }
  }

  appendClient(client: CompanyListItemDto) {
    if (!this.selectedClients.some(c => c.id === client.id)) {
      this.selectedClients.push(client)
      this.form.get("clientIds")?.setValue(this.selectedClients.map(c => c.id))
      this.form.get("clientIds")?.markAsDirty()
      this.loadCustomersForSelectedClients()
    }
  }

  removeClient(client: CompanyListItemDto) {
    if (this.selectedClients.includes(client)) {
      this.selectedClients = this.selectedClients.filter(c => c !== client)
      this.form.get("clientIds")?.setValue(this.selectedClients.map(c => c.id))
      this.form.get("clientIds")?.markAsDirty()
      this.loadCustomersForSelectedClients()
    }
  }

  appendCustomer(customer: CustomerListItemDto) {
    if (!this.selectedCustomers.some(c => c.id === customer.id)) {
      this.selectedCustomers.push(customer)
      this.form.get("customerIds")?.setValue(this.selectedCustomers.map(c => c.id))
      this.form.get("customerIds")?.markAsDirty()
    }
  }

  removeCustomer(customer: CustomerListItemDto) {
    if (this.selectedCustomers.includes(customer)) {
      this.selectedCustomers = this.selectedCustomers.filter(c => c !== customer)
      this.form.get("customerIds")?.setValue(this.selectedCustomers.map(c => c.id))
      this.form.get("customerIds")?.markAsDirty()
    }
  }

  setRole(role: string | undefined) {
    this.selectedRole = role as string;
    if (this.isAnySeesAllRole()) {
      this.ensureAnyTagSelected();
    }
  }
}
